import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CircularProgress,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material'

import errorHandlerHook from '@hooks/useErrorHandler'

import PressPostService from '@services/press-post'

import { TLGTypography } from '@components/TLGTypography'

import LinkUtils from '@utils/links'
import { IPressPost } from '@utils/types'

import { listItemSx, listItemTextSx, listSx, loadingWrapperSx } from './styles'

const RecentPressPosts = () => {
  const { t } = useTranslation()
  const { handleError } = errorHandlerHook.useErrorHandler()

  const [isLoadingRecentPressPosts, setIsLoadingRecentPressPosts] =
    useState(false)
  const [recentPressPosts, setRecentPressPosts] = useState<IPressPost[]>([])

  const fetchRecentPressPosts = useCallback(async () => {
    setIsLoadingRecentPressPosts(true)

    try {
      const { data } = await PressPostService.findAllAsVisitor({
        page: 1,
        limit: 4,
      })

      setRecentPressPosts(data)
    } catch (err) {
      handleError({ exception: err })
    }

    setIsLoadingRecentPressPosts(false)
  }, [handleError])

  useEffect(() => {
    fetchRecentPressPosts()
  }, [fetchRecentPressPosts])

  const isRecentPressPostsEmpty = recentPressPosts.length === 0

  return (
    <Stack>
      <TLGTypography variant="h4" component="h2">
        {t('Recent Press')}
      </TLGTypography>
      {isLoadingRecentPressPosts ? (
        <Stack sx={loadingWrapperSx}>
          <CircularProgress />
        </Stack>
      ) : (
        <>
          {isRecentPressPostsEmpty ? (
            <TLGTypography variant="h6" component="h3">
              {t('No recent press posts found')}
            </TLGTypography>
          ) : (
            <List sx={listSx}>
              {recentPressPosts.map((pressPost) => (
                <ListItem
                  key={pressPost.externalUrl}
                  data-testid="recent-press-post-item"
                  title={pressPost.title}
                  component="a"
                  href={pressPost.externalUrl}
                  target="_blank"
                  rel={`noopener noreferrer ${
                    LinkUtils.getLinkRel() ?? ''
                  }`.trim()}
                  sx={listItemSx}
                >
                  <Typography sx={listItemTextSx}>{pressPost.title}</Typography>
                </ListItem>
              ))}
            </List>
          )}
        </>
      )}
    </Stack>
  )
}

export { RecentPressPosts }
